import { useState } from 'react';
import { Download } from 'lucide-react';
import { getFilenameFromUrl } from '@/lib/imageUtils';

interface ImageCardProps {
  url: string;
  compact?: boolean;
  naturalLoading?: boolean;
  onOpenLightbox: () => void;
}

export default function ImageCard({ url, compact = false, naturalLoading = false, onOpenLightbox }: ImageCardProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [dimensions, setDimensions] = useState<{ w: number; h: number } | null>(null);

  const filename = getFilenameFromUrl(url);
  const thumbnailSize = compact ? 300 : 400;

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setDimensions({
      w: img.naturalWidth,
      h: img.naturalHeight,
    });
    setIsLoading(false);
  };

  const handleImageError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  const handleImageClick = () => {
    onOpenLightbox();
  };

  const handleImageKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleImageClick();
    }
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    a.click();
  };

  // In natural loading mode, hide failed images
  if (naturalLoading && hasError) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Image Container */}
      <div
        role="button"
        tabIndex={0}
        className="relative rounded-lg overflow-hidden border border-border bg-muted/50 cursor-pointer group"
        style={{ maxHeight: `${thumbnailSize}px` }}
        onClick={handleImageClick}
        onKeyDown={handleImageKeyDown}
        aria-label={`View ${filename} full size`}
      >
        {/* Loading Skeleton */}
        {isLoading && (
          <div className="absolute inset-0 animate-pulse bg-muted" style={{ minHeight: '200px' }} />
        )}

        {/* Error State */}
        {hasError && !naturalLoading && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ minHeight: '200px' }}>
            <p className="text-sm text-muted-foreground">Failed to load image</p>
          </div>
        )}

        {/* Image */}
        {!hasError && (
          <img
            src={url}
            alt={filename}
            className="w-full h-auto object-contain"
            style={{ maxHeight: `${thumbnailSize}px` }}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading="lazy"
          />
        )}

      </div>

      {/* Image Metadata */}
      {!isLoading && !hasError && (
        <div className="space-y-2">
          {/* Filename */}
          <p className="text-sm font-medium text-foreground truncate" title={filename}>
            {filename}
          </p>

          {/* Dimensions */}
          {dimensions && (
            <p className="text-sm text-muted-foreground">
              {dimensions.w} × {dimensions.h}
            </p>
          )}

          {/* Download Button */}
          <button
            type="button"
            onClick={handleDownload}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </button>
        </div>
      )}
    </div>
  );
}
